import React, { useState, useEffect } from 'react';
import DashboardLayout from '@/components/DashboardLayout';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { SearchableSelect } from '@/components/ui/searchable-select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { ShoppingCart, ChevronDown, ChevronUp, Truck, Package, Clock, CheckCircle2, XCircle, Building2, Calendar, FileText, Printer, Eye, Edit2, History, Trash2, MessageCircle, Send, RotateCcw } from 'lucide-react';
import { formatDate } from '@/lib/utils';
import POPrint from '@/components/POPrint';
import PriceHistoryPopup from '@/components/PriceHistoryPopup';
import { formatCurrency, formatQuantity, formatRate } from '@/utils/formatters';
import DeepLinkBackButton from '@/components/DeepLinkBackButton';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusColors = {
  draft: 'bg-gray-100 text-gray-600 border-gray-200',
  pending_approval: 'bg-orange-100 text-orange-700 border-orange-200',
  clarification_requested: 'bg-purple-100 text-purple-700 border-purple-200',
  approved: 'bg-blue-100 text-blue-700 border-blue-200',
  issued: 'bg-blue-100 text-blue-700 border-blue-200',
  partial: 'bg-yellow-100 text-yellow-700 border-yellow-200',
  received: 'bg-green-100 text-green-700 border-green-200',
  // Bug 8 fix: 'complete' = fully received via GRN (green, positive).
  // 'closed' = manually short-closed before full receipt (slate, neutral-
  // terminal, visually distinct from 'complete').
  complete: 'bg-green-100 text-green-700 border-green-200',
  closed: 'bg-slate-200 text-slate-700 border-slate-300',
  rejected: 'bg-red-100 text-red-500 border-red-200',
  cancelled: 'bg-red-100 text-red-500 border-red-200'
};

const statusIcons = {
  draft: <FileText className="w-4 h-4" />,
  pending_approval: <Clock className="w-4 h-4" />,
  clarification_requested: <MessageCircle className="w-4 h-4" />,
  approved: <CheckCircle2 className="w-4 h-4" />,
  issued: <Truck className="w-4 h-4" />,
  partial: <Clock className="w-4 h-4" />,
  received: <CheckCircle2 className="w-4 h-4" />,
  complete: <CheckCircle2 className="w-4 h-4" />,
  closed: <XCircle className="w-4 h-4" />,
  rejected: <XCircle className="w-4 h-4" />,
  cancelled: <XCircle className="w-4 h-4" />
};

const statusLabels = {
  draft: 'Draft',
  pending_approval: 'Pending Approval',
  clarification_requested: 'Clarification Requested',
  approved: 'Approved',
  issued: 'Issued',
  partial: 'Partial',
  received: 'Received',
  complete: 'Complete',
  closed: 'Closed',
  rejected: 'Rejected',
  cancelled: 'Cancelled'
};

export default function PurchaseOrdersPage() {
  const { user, getAuthHeader } = useAuth();
  const [purchaseOrders, setPurchaseOrders] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [expandedPO, setExpandedPO] = useState(null); // Single expanded PO (accordion style)
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterSupplier, setFilterSupplier] = useState('all');
  const [itemSearch, setItemSearch] = useState(''); // Item name search
  const [selectedPO, setSelectedPO] = useState(null);
  const [openDetails, setOpenDetails] = useState(false);
  const [printPO, setPrintPO] = useState(null);
  
  // Revision state
  const [revisionPO, setRevisionPO] = useState(null);
  const [revisionItems, setRevisionItems] = useState({});
  const [revisionReason, setRevisionReason] = useState('');
  const [revising, setRevising] = useState(false);

  // Edit PO state
  const [editPO, setEditPO] = useState(null);
  const [editLineItems, setEditLineItems] = useState([]);
  const [editOtherChargesTaxable, setEditOtherChargesTaxable] = useState(0);
  const [editGstRate, setEditGstRate] = useState(18);
  const [editOtherChargesNonTaxable, setEditOtherChargesNonTaxable] = useState(0);
  const [editReason, setEditReason] = useState('');
  // When editing from clarification_requested, holds the creator's response to
  // the approver's last clarification query. Sent alongside the line-item edits.
  const [editClarificationResponse, setEditClarificationResponse] = useState('');
  const [editing, setEditing] = useState(false);

  // Price history popup state
  const [priceHistoryOpen, setPriceHistoryOpen] = useState(false);
  const [priceHistoryItem, setPriceHistoryItem] = useState(null);

  // Bug 9 — inline "Last Price" per line item (batched fetch on dialog open)
  const [lineItemLastPrices, setLineItemLastPrices] = useState({});

  // Clarification response state
  const [clarificationResponse, setClarificationResponse] = useState('');
  const [respondingToClarification, setRespondingToClarification] = useState(false);

  // Purchase history (GRNs) for selected PO
  const [purchaseHistory, setPurchaseHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);

  // Close PO dialog state
  const [closePODialog, setClosePODialog] = useState(null);
  const [closeReason, setCloseReason] = useState('');
  const [closingPO, setClosingPO] = useState(false);

  // Delete PO dialog state
  const [deletePODialog, setDeletePODialog] = useState(null);
  const [deletingPO, setDeletingPO] = useState(false);

  // Return Material state
  const [openReturnDialog, setOpenReturnDialog] = useState(false);
  const [returnItems, setReturnItems] = useState([]);
  const [submittingReturn, setSubmittingReturn] = useState(false);

  // UOM list for supplier UOM dropdown
  const [uomList, setUomList] = useState([]);

  const fetchData = async () => {
    try {
      const [posRes, suppliersRes, uomRes] = await Promise.all([
        axios.get(`${API_URL}/purchase-orders`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/suppliers`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/uom`, { headers: getAuthHeader() }).catch(() => ({ data: { uoms: [] } }))
      ]);
      setPurchaseOrders(posRes.data);
      setSuppliers(suppliersRes.data);
      setUomList(uomRes.data?.uoms || uomRes.data || []);
    } catch (error) {
      console.error('Fetch error:', error);
      toast.error('Failed to fetch data');
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const togglePOExpand = (poId) => {
    setExpandedPO(prev => prev === poId ? null : poId);
  };

  const handleStatusChange = async (poId, newStatus) => {
    try {
      await axios.patch(`${API_URL}/purchase-orders/${poId}`, {
        status: newStatus
      }, { headers: getAuthHeader() });
      toast.success('PO status updated');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update status');
    }
  };

  const viewPODetails = async (po) => {
    setSelectedPO(po);
    setOpenDetails(true);
    setPurchaseHistory([]);
    setLineItemLastPrices({});

    // Bug 9 — batched fetch of last_same_supplier price per line item
    if (po.line_items && po.line_items.length > 0) {
      try {
        const results = await Promise.all(
          po.line_items.map(async (li) => {
            if (!li.item_id) return [li.item_id, null];
            try {
              const params = new URLSearchParams();
              if (po.supplier_id) params.append('supplier_id', po.supplier_id);
              if (po.supplier_name) params.append('supplier_name', po.supplier_name);
              const res = await axios.get(
                `${API_URL}/store/price-history/${li.item_id}?${params.toString()}`,
                { headers: getAuthHeader() }
              );
              const lastSame = res.data?.last_same_supplier;
              const fallbackAvg = res.data?.avg_all_suppliers;
              return [li.item_id, {
                price: lastSame?.price ?? fallbackAvg?.price ?? null,
                source: lastSame ? 'same_supplier' : (fallbackAvg ? 'avg_all' : null),
                date: lastSame?.date ?? null,
              }];
            } catch {
              return [li.item_id, null];
            }
          })
        );
        const map = {};
        results.forEach(([k, v]) => { if (k) map[k] = v; });
        setLineItemLastPrices(map);
      } catch (e) {
        // silent — column just renders '-' when missing
      }
    }
    
    // Fetch purchase history (GRNs) for this PO
    setLoadingHistory(true);
    try {
      const response = await axios.get(`${API_URL}/store/purchase-entries?po_id=${po.id}`, { 
        headers: getAuthHeader() 
      });
      setPurchaseHistory(response.data || []);
    } catch (error) {
      console.error('Failed to fetch purchase history:', error);
      setPurchaseHistory([]);
    } finally {
      setLoadingHistory(false);
    }
  };

  const handleRespondToClarification = async () => {
    if (!clarificationResponse.trim()) {
      toast.error('Please enter your response');
      return;
    }
    
    setRespondingToClarification(true);
    try {
      await axios.post(`${API_URL}/purchase-orders/${selectedPO.id}/respond-clarification`, {
        response: clarificationResponse.trim()
      }, { headers: getAuthHeader() });
      toast.success('Response submitted. PO resubmitted for approval.');
      setClarificationResponse('');
      setOpenDetails(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit response');
    } finally {
      setRespondingToClarification(false);
    }
  };

  const openRevisionDialog = (po) => {
    // Initialize revision items with current quantities
    const items = {};
    po.line_items?.forEach(item => {
      items[item.item_id] = {
        item_name: item.item_name,
        current_qty: item.quantity,
        new_qty: item.quantity,
        received_qty: item.quantity_received || 0,
        unit: item.unit,
        unit_price: item.unit_price
      };
    });
    setRevisionItems(items);
    setRevisionReason('');
    setRevisionPO(po);
  };

  const handleRevision = async () => {
    // Validate that something has changed
    const changes = Object.entries(revisionItems)
      .filter(([itemId, data]) => parseFloat(data.new_qty) !== data.current_qty)
      .map(([itemId, data]) => ({
        item_id: itemId,
        new_quantity: parseFloat(data.new_qty) || 0
      }));

    if (changes.length === 0) {
      toast.error('No quantity changes to submit');
      return;
    }

    if (!revisionReason.trim()) {
      toast.error('Please provide a reason for the revision');
      return;
    }

    setRevising(true);
    try {
      const response = await axios.post(
        `${API_URL}/purchase-orders/${revisionPO.id}/revise`,
        {
          items: changes,
          revision_reason: revisionReason.trim()
        },
        { headers: getAuthHeader() }
      );
      
      toast.success(`PO revised successfully. New total: ₹${response.data.new_total?.toLocaleString()}`);
      setRevisionPO(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to revise PO');
    } finally {
      setRevising(false);
    }
  };

  // Open Edit PO dialog
  const openEditDialog = (po) => {
    // Initialize edit line items from PO
    const items = po.line_items?.map(item => ({
      indent_id: item.indent_id,
      item_name: item.item_name,
      indent_number: item.indent_number,
      supplier_item_name: item.supplier_item_name || '',
      indent_quantity: item.indent_quantity || item.quantity,
      system_qty_ordered: item.system_qty_ordered || item.indent_quantity || item.quantity,
      indent_unit: item.indent_unit || item.unit,
      quantity: item.quantity,
      unit: item.unit,
      unit_price: item.unit_price,
      amount: item.amount || (item.quantity * item.unit_price)
    })) || [];
    
    setEditLineItems(items);
    setEditOtherChargesTaxable(po.other_charges_taxable || 0);
    setEditGstRate(po.gst_rate || 18);
    setEditOtherChargesNonTaxable(po.other_charges_non_taxable || 0);
    setEditReason('');
    setEditClarificationResponse('');
    setEditPO(po);
  };

  // Update edit line item
  const updateEditLineItem = (index, field, value) => {
    setEditLineItems(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      // Recalculate amount
      if (field === 'quantity' || field === 'unit_price') {
        updated[index].amount = (parseFloat(updated[index].quantity) || 0) * (parseFloat(updated[index].unit_price) || 0);
      }
      return updated;
    });
  };

  // Calculate edit totals
  const calculateEditTotals = () => {
    const subTotal = editLineItems.reduce((sum, item) => sum + (item.amount || 0), 0);
    const taxableAmount = subTotal + parseFloat(editOtherChargesTaxable || 0);
    const gstAmount = taxableAmount * (parseFloat(editGstRate || 0) / 100);
    const total = subTotal + parseFloat(editOtherChargesTaxable || 0) + gstAmount + parseFloat(editOtherChargesNonTaxable || 0);
    return { subTotal, taxableAmount, gstAmount, total };
  };

  // Handle Edit PO submission
  const handleEditPO = async () => {
    if (!editPO) return;
    
    // Validate
    if (editLineItems.some(item => !item.quantity || item.quantity <= 0)) {
      toast.error('All items must have valid quantities');
      return;
    }
    
    if (editLineItems.some(item => !item.unit_price || item.unit_price <= 0)) {
      toast.error('All items must have valid prices');
      return;
    }

    // If editing after approval, reason is required
    const needsReason = ['approved', 'issued', 'partial'].includes(editPO.status);
    if (needsReason && !editReason.trim()) {
      toast.error('Please provide a reason for editing this approved PO');
      return;
    }

    // If editing from clarification_requested, creator's response is required
    const isFromClarification = editPO.status === 'clarification_requested';
    if (isFromClarification && !editClarificationResponse.trim()) {
      toast.error('Please enter your response to the clarification request');
      return;
    }

    setEditing(true);
    try {
      const payload = {
        line_items: editLineItems.map(item => ({
          indent_id: item.indent_id,
          quantity: parseFloat(item.quantity),
          unit: item.unit,
          indent_quantity: parseFloat(item.indent_quantity),
          system_qty_ordered: parseFloat(item.system_qty_ordered) || parseFloat(item.indent_quantity),
          indent_unit: item.indent_unit,
          unit_price: parseFloat(item.unit_price),
          supplier_item_name: item.supplier_item_name || null
        })),
        other_charges_taxable: parseFloat(editOtherChargesTaxable) || 0,
        gst_rate: parseFloat(editGstRate) || 18,
        other_charges_non_taxable: parseFloat(editOtherChargesNonTaxable) || 0,
        edit_reason: editReason.trim() || null,
        clarification_response: isFromClarification ? editClarificationResponse.trim() : null
      };

      const response = await axios.put(
        `${API_URL}/purchase-orders/${editPO.id}/edit`,
        payload,
        { headers: getAuthHeader() }
      );

      if (isFromClarification) {
        toast.success(`PO ${editPO.po_number} updated and resubmitted for approval`);
      } else if (response.data.requires_reapproval) {
        toast.success(`PO ${editPO.po_number} updated and sent for re-approval`);
      } else {
        toast.success(`PO ${editPO.po_number} updated successfully`);
      }

      setEditPO(null);
      fetchData();
    } catch (error) {
      // Keep dialog open on failure so operator can retry or cancel.
      const detail = error.response?.data?.detail;
      const msg = typeof detail === 'string'
        ? detail
        : (Array.isArray(detail) ? detail.map(d => d?.msg || JSON.stringify(d)).join(', ') : 'Failed to update PO');
      toast.error(msg);
    } finally {
      setEditing(false);
    }
  };

  // Handle resubmit rejected PO
  const handleResubmitPO = async (po) => {
    try {
      await axios.post(
        `${API_URL}/purchase-orders/${po.id}/resubmit`,
        {},
        { headers: getAuthHeader() }
      );
      toast.success(`PO ${po.po_number} resubmitted for approval`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to resubmit PO');
    }
  };

  // Handle close/complete PO manually
  const handleClosePO = async () => {
    if (!closePODialog || !closeReason.trim()) {
      toast.error('Please provide a reason for closing the PO');
      return;
    }
    
    setClosingPO(true);
    try {
      const response = await axios.post(
        `${API_URL}/purchase-orders/${closePODialog.id}/close`,
        { reason: closeReason.trim() },
        { headers: getAuthHeader() }
      );
      
      const shortClosed = response.data.short_closed;
      if (shortClosed) {
        toast.success(`PO ${closePODialog.po_number} closed with short shipment recorded`);
      } else {
        toast.success(`PO ${closePODialog.po_number} closed successfully`);
      }
      
      setClosePODialog(null);
      setCloseReason('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to close PO');
    } finally {
      setClosingPO(false);
    }
  };

  // Handle delete PO
  const handleDeletePO = async () => {
    if (!deletePODialog) return;
    
    setDeletingPO(true);
    try {
      await axios.delete(
        `${API_URL}/purchase-orders/${deletePODialog.id}`,
        { headers: getAuthHeader() }
      );
      
      toast.success(`PO ${deletePODialog.po_number} deleted successfully`);
      setDeletePODialog(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete PO');
    } finally {
      setDeletingPO(false);
    }
  };

  // ===== RETURN MATERIAL =====
  const openReturnMaterial = () => {
    if (!selectedPO) return;
    const items = (selectedPO.line_items || [])
      .filter(li => (li.system_qty_received || 0) > 0)
      .map(li => ({
        item_id: li.item_id,
        item_name: li.item_name,
        received: li.system_qty_received || 0,
        unit: li.indent_unit || li.unit || 'NOS',
        qty_to_return: 0,
        reason: ''
      }));
    setReturnItems(items);
    setOpenReturnDialog(true);
  };

  const handleSubmitReturn = async () => {
    const itemsToReturn = returnItems.filter(i => i.qty_to_return > 0);
    if (itemsToReturn.length === 0) {
      toast.error('Enter return quantity for at least one item');
      return;
    }
    const missingReason = itemsToReturn.find(i => !i.reason.trim());
    if (missingReason) {
      toast.error(`Enter reason for ${missingReason.item_name}`);
      return;
    }
    setSubmittingReturn(true);
    try {
      const payload = {
        items: itemsToReturn.map(i => ({ item_id: i.item_id, qty_to_return: i.qty_to_return, reason: i.reason }))
      };
      await axios.post(`${API_URL}/purchase-orders/${selectedPO.id}/return-material`, payload, { headers: getAuthHeader() });
      toast.success('Return demand created. Store will be notified for material release.');
      setOpenReturnDialog(false);
      setOpenDetails(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create return');
    } finally {
      setSubmittingReturn(false);
    }
  };

  // Filter POs
  const filteredPOs = purchaseOrders.filter(po => {
    if (filterStatus !== 'all' && po.status !== filterStatus) return false;
    if (filterSupplier !== 'all' && po.supplier_id !== filterSupplier) return false;
    // Item name search - search within line items
    if (itemSearch.trim()) {
      const searchLower = itemSearch.toLowerCase();
      const hasMatchingItem = po.line_items?.some(item => 
        (item.item_name || '').toLowerCase().includes(searchLower)
      );
      if (!hasMatchingItem) return false;
    }
    return true;
  });

  // Summary stats
  const stats = {
    total: purchaseOrders.length,
    issued: purchaseOrders.filter(po => po.status === 'issued').length,
    partial: purchaseOrders.filter(po => po.status === 'partial').length,
    received: purchaseOrders.filter(po => po.status === 'received').length,
    totalValue: purchaseOrders.reduce((sum, po) => sum + (po.total_amount || 0), 0)
  };

  if (!user) {
    return (
      <DashboardLayout>
        <div className="text-center py-12">
          <p className="text-muted-foreground">You don&apos;t have permission to access this page</p>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <DeepLinkBackButton />
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold">Purchase Orders</h1>
            <p className="text-muted-foreground">Track and manage purchase orders</p>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-5 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <ShoppingCart className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.total}</p>
                  <p className="text-sm text-muted-foreground">Total POs</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <Truck className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.issued}</p>
                  <p className="text-sm text-muted-foreground">Issued</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-orange-100 rounded-lg">
                  <Clock className="w-5 h-5 text-orange-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.partial}</p>
                  <p className="text-sm text-muted-foreground">Partial</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-100 rounded-lg">
                  <CheckCircle2 className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.received}</p>
                  <p className="text-sm text-muted-foreground">Received</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <Package className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">₹{stats.totalValue.toLocaleString()}</p>
                  <p className="text-sm text-muted-foreground">Total Value</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <div className="flex gap-4 items-center flex-wrap">
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">Status:</span>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-36">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="issued">Issued</SelectItem>
                <SelectItem value="partial">Partial</SelectItem>
                <SelectItem value="received">Received</SelectItem>
                <SelectItem value="cancelled">Cancelled</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">Supplier:</span>
            <SearchableSelect
              options={[
                { value: 'all', label: 'All Suppliers' },
                ...suppliers.map(sup => ({
                  value: sup.id,
                  label: sup.name
                }))
              ]}
              value={filterSupplier}
              onValueChange={setFilterSupplier}
              placeholder="Select supplier"
              searchPlaceholder="Type to search suppliers..."
              emptyText="No suppliers found"
              className="w-48"
            />
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">Item:</span>
            <Input
              placeholder="Search by item name..."
              value={itemSearch}
              onChange={(e) => setItemSearch(e.target.value)}
              className="w-48"
            />
          </div>
        </div>

        {/* PO Table */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <ShoppingCart className="w-5 h-5" />
              Purchase Orders ({filteredPOs.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-8"></TableHead>
                  <TableHead>PO Number</TableHead>
                  <TableHead>Supplier</TableHead>
                  <TableHead>PO Date</TableHead>
                  <TableHead>Delivery Date</TableHead>
                  <TableHead className="text-center">Items</TableHead>
                  <TableHead>Linked SO</TableHead>
                  <TableHead className="text-right">Amount</TableHead>
                  <TableHead className="text-center">Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredPOs.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={10} className="text-center py-12">
                      <ShoppingCart className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50" />
                      <p className="text-muted-foreground">No purchase orders yet</p>
                      <p className="text-sm text-muted-foreground mt-1">Create POs from the Indent Register</p>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredPOs.map((po) => {
                    const isExpanded = expandedPO === po.id;
                    return (
                      <React.Fragment key={po.id}>
                        <TableRow className="hover:bg-muted/30">
                          <TableCell>
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              className="p-1 h-6 w-6"
                              onClick={() => togglePOExpand(po.id)}
                            >
                              {isExpanded ? (
                                <ChevronUp className="w-4 h-4" />
                              ) : (
                                <ChevronDown className="w-4 h-4" />
                              )}
                            </Button>
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline" className="font-mono">{po.po_number}</Badge>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <Building2 className="w-4 h-4 text-muted-foreground" />
                              {po.supplier_name}
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-1 text-sm">
                              <Calendar className="w-3 h-3 text-muted-foreground" />
                              {formatDate(po.po_date)}
                            </div>
                          </TableCell>
                          <TableCell>
                            {po.expected_delivery_date ? (
                              <div className="flex items-center gap-1 text-sm">
                                <Truck className="w-3 h-3 text-muted-foreground" />
                                {formatDate(po.expected_delivery_date)}
                              </div>
                            ) : (
                              <span className="text-muted-foreground text-sm">-</span>
                            )}
                          </TableCell>
                          <TableCell className="text-center">
                            <Badge variant="secondary">{po.line_items?.length || 0}</Badge>
                          </TableCell>
                          <TableCell>
                            {(() => {
                              const soNums = [...new Set((po.line_items || []).flatMap(li => li.so_numbers || (li.so_number ? [li.so_number] : [])))].filter(Boolean);
                              return soNums.length > 0
                                ? <div className="flex flex-wrap gap-1">{soNums.map(s => <Badge key={s} variant="outline" className="text-xs bg-blue-50 text-blue-700 border-blue-200">{s}</Badge>)}</div>
                                : <span className="text-xs text-muted-foreground">Stock</span>;
                            })()}
                          </TableCell>
                          <TableCell className="text-right font-medium">
                            ₹{(po.total_amount || 0).toLocaleString()}
                          </TableCell>
                          <TableCell className="text-center">
                            <div className="space-y-1">
                              <Badge className={statusColors[po.status]}>
                                <span className="flex items-center gap-1">
                                  {statusIcons[po.status]}
                                  {statusLabels[po.status] || po.status}
                                </span>
                              </Badge>
                              {/* Show receiving progress for issued/partial POs */}
                              {(po.status === 'issued' || po.status === 'partial' || po.status === 'approved') && po.total_ordered_qty > 0 && (
                                <div className="text-xs text-muted-foreground">
                                  {po.total_received_qty || 0}/{po.total_ordered_qty} received
                                </div>
                              )}
                            </div>
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex justify-end gap-1">
                              <Button size="sm" variant="ghost" onClick={() => viewPODetails(po)} title="View">
                                <Eye className="w-4 h-4" />
                              </Button>
                              {/* Edit button - for PO creator or owner */}
                              {['pending_approval', 'approved', 'issued', 'partial', 'rejected', 'clarification_requested'].includes(po.status) && 
                               (po.created_by === user?.id || user?.is_owner) && (
                                <Button 
                                  size="sm" 
                                  variant="ghost" 
                                  onClick={() => openEditDialog(po)}
                                  className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                                  data-testid={`edit-po-${po.po_number}`}
                                  title="Edit"
                                >
                                  <Edit2 className="w-4 h-4" />
                                </Button>
                              )}
                              {/* Resubmit button - for rejected POs */}
                              {po.status === 'rejected' && po.created_by === user?.id && (
                                <Button 
                                  size="sm" 
                                  variant="ghost" 
                                  onClick={() => handleResubmitPO(po)}
                                  className="text-green-600 hover:text-green-700 hover:bg-green-50"
                                  data-testid={`resubmit-po-${po.po_number}`}
                                  title="Resubmit"
                                >
                                  <CheckCircle2 className="w-4 h-4" />
                                </Button>
                              )}
                              {/* Print button - only for approved/issued POs */}
                              {['approved', 'issued', 'partial', 'received'].includes(po.status) && (
                                <Button size="sm" variant="ghost" onClick={() => setPrintPO(po)} title="Print">
                                  <Printer className="w-4 h-4" />
                                </Button>
                              )}
                              {/* Close PO button - for approved/issued/partial POs */}
                              {['approved', 'issued', 'partial'].includes(po.status) && (
                                <Button 
                                  size="sm" 
                                  variant="ghost" 
                                  onClick={() => setClosePODialog(po)}
                                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                  data-testid={`close-po-${po.po_number}`}
                                  title="Close PO"
                                >
                                  <XCircle className="w-4 h-4" />
                                </Button>
                              )}
                              {/* Delete button - owner always, creator before approval */}
                              {(user?.is_owner || 
                                (po.created_by === user?.id && ['draft', 'pending_approval', 'rejected'].includes(po.status))
                              ) && (
                                <Button 
                                  size="sm" 
                                  variant="ghost" 
                                  onClick={() => setDeletePODialog(po)}
                                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                  data-testid={`delete-po-${po.po_number}`}
                                  title="Delete PO"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                        
                        {/* Expanded Line Items */}
                        {isExpanded && po.line_items?.map((item, idx) => (
                          <TableRow key={`${po.id}-${idx}`} className="bg-muted/20 text-sm">
                            <TableCell></TableCell>
                            <TableCell colSpan={2} className="pl-10">
                              <div className="flex items-center gap-2">
                                <span className="text-muted-foreground">↳</span>
                                <span>{item.item_name}</span>
                                <Badge variant="outline" className="text-xs font-mono">{item.indent_number}</Badge>
                                {(item.so_numbers?.length > 0 || item.so_number) && (
                                  (item.so_numbers || [item.so_number]).filter(Boolean).map(s => (
                                    <Badge key={s} variant="outline" className="text-xs bg-blue-50 text-blue-700 border-blue-200">SO: {s}</Badge>
                                  ))
                                )}
                              </div>
                            </TableCell>
                            <TableCell colSpan={2}>
                              <span className="text-muted-foreground">{item.quantity} {item.unit}</span>
                            </TableCell>
                            <TableCell className="text-right">₹{item.unit_price}</TableCell>
                            <TableCell className="text-right font-medium">₹{item.amount?.toLocaleString()}</TableCell>
                            <TableCell colSpan={2}></TableCell>
                          </TableRow>
                        ))}
                      </React.Fragment>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        {/* PO Details Dialog */}
        <Dialog open={openDetails} onOpenChange={setOpenDetails}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <ShoppingCart className="w-5 h-5" />
                Purchase Order Details
              </DialogTitle>
              <DialogDescription>
                {selectedPO?.po_number}
              </DialogDescription>
            </DialogHeader>
            {selectedPO && (
              <div className="space-y-6 py-4">
                {/* Header Info */}
                <div className="grid grid-cols-2 gap-4 p-4 bg-muted/30 rounded-lg">
                  <div>
                    <p className="text-sm text-muted-foreground">Supplier</p>
                    <p className="font-medium">{selectedPO.supplier_name}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Status</p>
                    <Badge className={statusColors[selectedPO.status]}>{selectedPO.status}</Badge>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">PO Date</p>
                    <p>{formatDate(selectedPO.po_date)}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Expected Delivery</p>
                    <p>{selectedPO.expected_delivery_date ? formatDate(selectedPO.expected_delivery_date) : '-'}</p>
                  </div>
                  {selectedPO.work_order_number && (
                    <div className="col-span-2">
                      <p className="text-sm text-muted-foreground">Linked Work Order</p>
                      <Badge variant="outline">{selectedPO.work_order_number}</Badge>
                    </div>
                  )}
                </div>

                {/* Line Items */}
                <div>
                  <p className="font-medium mb-2">Line Items</p>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Item</TableHead>
                        <TableHead>Indent #</TableHead>
                        <TableHead className="text-right">Supplier Qty</TableHead>
                        <TableHead className="text-center">Supplier UOM</TableHead>
                        <TableHead className="text-right">PO Qty</TableHead>
                        <TableHead className="text-center">System UOM</TableHead>
                        <TableHead className="text-right">Unit Price</TableHead>
                        <TableHead className="text-right">Last Price</TableHead>
                        <TableHead className="text-right">Amount</TableHead>
                        {selectedPO?.status === 'pending_approval' && (
                          <TableHead className="text-center w-16">History</TableHead>
                        )}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {selectedPO.line_items?.map((item, idx) => (
                        <TableRow key={idx}>
                          <TableCell>
                            <div>{item.item_name}</div>
                            {item.supplier_item_name && (
                              <div className="text-xs text-muted-foreground italic">({item.supplier_item_name})</div>
                            )}
                            {(item.so_numbers?.length > 0 || item.so_number) && (
                              <div className="flex flex-wrap gap-1 mt-1">
                                {(item.so_numbers || [item.so_number]).filter(Boolean).map(s => (
                                  <Badge key={s} variant="outline" className="text-xs bg-blue-50 text-blue-700 border-blue-200">SO: {s}</Badge>
                                ))}
                              </div>
                            )}
                            {/* Show original indent qty as reference if different from PO qty */}
                            {item.indent_quantity && item.indent_quantity !== (item.system_qty_ordered || item.indent_quantity) && (
                              <div className="text-xs text-blue-600">Indent: {item.indent_quantity} {item.indent_unit}</div>
                            )}
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline" className="font-mono text-xs">{item.indent_number}</Badge>
                          </TableCell>
                          <TableCell className="text-right">{item.quantity}</TableCell>
                          <TableCell className="text-center">
                            <Badge variant="secondary">{item.unit || '-'}</Badge>
                          </TableCell>
                          <TableCell className="text-right font-medium">{item.system_qty_ordered || item.indent_quantity || item.quantity}</TableCell>
                          <TableCell className="text-center">
                            <Badge variant="outline">{item.indent_unit || item.unit || '-'}</Badge>
                          </TableCell>
                          <TableCell className="text-right">₹{item.unit_price}</TableCell>
                          <TableCell className="text-right text-xs">
                            {(() => {
                              const lp = lineItemLastPrices[item.item_id];
                              if (!lp || lp.price == null) return <span className="text-gray-400">-</span>;
                              const cur = parseFloat(item.unit_price) || 0;
                              const delta = cur - lp.price;
                              const pct = lp.price > 0 ? (delta / lp.price) * 100 : 0;
                              const color = pct <= 0 ? 'text-green-700' : (pct <= 5 ? 'text-yellow-700' : 'text-red-700');
                              return (
                                <div className="flex flex-col items-end" data-testid={`po-last-price-${item.item_id}`}>
                                  <span className="font-medium">₹{lp.price.toFixed(2)}</span>
                                  <span className={`text-[10px] ${color}`}>
                                    {delta >= 0 ? '+' : ''}{pct.toFixed(1)}%
                                    {lp.source === 'avg_all' && <span className="text-gray-500"> (avg)</span>}
                                  </span>
                                </div>
                              );
                            })()}
                          </TableCell>
                          <TableCell className="text-right font-medium">₹{item.amount?.toLocaleString()}</TableCell>
                          {selectedPO?.status === 'pending_approval' && (
                            <TableCell className="text-center">
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                className="h-8 w-8 p-0"
                                onClick={() => {
                                  setPriceHistoryItem({
                                    itemId: item.item_id,
                                    itemName: item.item_name,
                                    supplierId: selectedPO.supplier_id,
                                    supplierName: selectedPO.supplier_name,
                                    currentPrice: parseFloat(item.unit_price) || 0,
                                    unit: item.unit
                                  });
                                  setPriceHistoryOpen(true);
                                }}
                                title="View Price History"
                              >
                                <History className="h-4 w-4 text-blue-600" />
                              </Button>
                            </TableCell>
                          )}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                  
                  {/* Totals Section - matching PO print format */}
                  <div className="mt-4 flex justify-end">
                    <div className="w-72 space-y-2 text-sm">
                      <div className="flex justify-between py-1">
                        <span className="text-muted-foreground">Sub Total:</span>
                        <span className="font-medium">{formatCurrency(selectedPO.sub_total || selectedPO.line_items?.reduce((sum, item) => sum + (item.amount || 0), 0) || 0)}</span>
                      </div>
                      {(selectedPO.other_charges_taxable > 0) && (
                        <div className="flex justify-between py-1">
                          <span className="text-muted-foreground">Other Charges (Taxable):</span>
                          <span>{formatCurrency(selectedPO.other_charges_taxable)}</span>
                        </div>
                      )}
                      <div className="flex justify-between py-1">
                        <span className="text-muted-foreground">GST ({selectedPO.gst_rate || 0}%):</span>
                        <span>{formatCurrency(selectedPO.gst_amount || 0)}</span>
                      </div>
                      {(selectedPO.other_charges_non_taxable > 0) && (
                        <div className="flex justify-between py-1">
                          <span className="text-muted-foreground">Other Charges:</span>
                          <span>{formatCurrency(selectedPO.other_charges_non_taxable)}</span>
                        </div>
                      )}
                      <div className="flex justify-between py-2 border-t font-bold text-base">
                        <span>Grand Total:</span>
                        <span>{formatCurrency(selectedPO.total_amount || 0)}</span>
                      </div>
                    </div>
                  </div>
                </div>

                {selectedPO.notes && (
                  <div>
                    <p className="text-sm text-muted-foreground">Notes</p>
                    <p>{selectedPO.notes}</p>
                  </div>
                )}

                {/* Purchase History & Charges Comparison */}
                {purchaseHistory.length > 0 && (
                  <div className="space-y-4 border-t pt-4">
                    {/* Charges Comparison */}
                    <div>
                      <p className="font-medium mb-3 flex items-center gap-2">
                        <History className="w-4 h-4" />
                        PO vs Actual Comparison
                      </p>
                      {(() => {
                        // Calculate actual totals from GRNs
                        const actualSubTotal = purchaseHistory.reduce((sum, grn) => 
                          sum + (grn.items?.reduce((itemSum, item) => itemSum + (item.system_amount || 0), 0) || 0), 0);
                        const actualOtherTaxable = purchaseHistory.reduce((sum, grn) => 
                          sum + (grn.other_charges_taxable || 0), 0);
                        const actualGst = purchaseHistory.reduce((sum, grn) => 
                          sum + (grn.gst_amount || 0), 0);
                        const actualOtherNonTaxable = purchaseHistory.reduce((sum, grn) => 
                          sum + (grn.other_charges_non_taxable || 0), 0);
                        const actualTotal = purchaseHistory.reduce((sum, grn) => 
                          sum + (grn.grand_total || 0), 0);
                        
                        const poSubTotal = selectedPO.sub_total || selectedPO.line_items?.reduce((sum, item) => sum + (item.amount || 0), 0) || 0;
                        
                        const formatVariance = (po, actual) => {
                          const diff = actual - po;
                          if (diff === 0) return <span className="text-muted-foreground">-</span>;
                          const color = diff > 0 ? 'text-red-600' : 'text-green-600';
                          const prefix = diff > 0 ? '+' : '';
                          return <span className={color}>{prefix}{formatCurrency(Math.abs(diff))}</span>;
                        };
                        
                        return (
                          <Table>
                            <TableHeader>
                              <TableRow>
                                <TableHead>Charge Type</TableHead>
                                <TableHead className="text-right">PO Amount</TableHead>
                                <TableHead className="text-right">Actual (GRNs)</TableHead>
                                <TableHead className="text-right">Variance</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              <TableRow>
                                <TableCell>Sub Total</TableCell>
                                <TableCell className="text-right">{formatCurrency(poSubTotal)}</TableCell>
                                <TableCell className="text-right">{formatCurrency(actualSubTotal)}</TableCell>
                                <TableCell className="text-right">{formatVariance(poSubTotal, actualSubTotal)}</TableCell>
                              </TableRow>
                              {(selectedPO.other_charges_taxable > 0 || actualOtherTaxable > 0) && (
                                <TableRow>
                                  <TableCell>Other Charges (Taxable)</TableCell>
                                  <TableCell className="text-right">{formatCurrency(selectedPO.other_charges_taxable || 0)}</TableCell>
                                  <TableCell className="text-right">{formatCurrency(actualOtherTaxable)}</TableCell>
                                  <TableCell className="text-right">{formatVariance(selectedPO.other_charges_taxable || 0, actualOtherTaxable)}</TableCell>
                                </TableRow>
                              )}
                              <TableRow>
                                <TableCell>GST ({selectedPO.gst_rate || 0}%)</TableCell>
                                <TableCell className="text-right">{formatCurrency(selectedPO.gst_amount || 0)}</TableCell>
                                <TableCell className="text-right">{formatCurrency(actualGst)}</TableCell>
                                <TableCell className="text-right">{formatVariance(selectedPO.gst_amount || 0, actualGst)}</TableCell>
                              </TableRow>
                              {(selectedPO.other_charges_non_taxable > 0 || actualOtherNonTaxable > 0) && (
                                <TableRow>
                                  <TableCell>Other Charges (Non-Taxable)</TableCell>
                                  <TableCell className="text-right">{formatCurrency(selectedPO.other_charges_non_taxable || 0)}</TableCell>
                                  <TableCell className="text-right">{formatCurrency(actualOtherNonTaxable)}</TableCell>
                                  <TableCell className="text-right">{formatVariance(selectedPO.other_charges_non_taxable || 0, actualOtherNonTaxable)}</TableCell>
                                </TableRow>
                              )}
                              <TableRow className="font-bold border-t-2">
                                <TableCell>Grand Total</TableCell>
                                <TableCell className="text-right">{formatCurrency(selectedPO.total_amount || 0)}</TableCell>
                                <TableCell className="text-right">{formatCurrency(actualTotal)}</TableCell>
                                <TableCell className="text-right">{formatVariance(selectedPO.total_amount || 0, actualTotal)}</TableCell>
                              </TableRow>
                            </TableBody>
                          </Table>
                        );
                      })()}
                    </div>

                    {/* Pending Quantities */}
                    <div>
                      <p className="font-medium mb-3">Quantity Status by Item (System UOM)</p>
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Item</TableHead>
                            <TableHead className="text-right">Ordered (System)</TableHead>
                            <TableHead className="text-right">Received (System)</TableHead>
                            <TableHead className="text-right">Pending</TableHead>
                            <TableHead>Status</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {selectedPO.line_items?.map((item, idx) => {
                            const ordered = item.system_qty_ordered || item.indent_quantity || item.quantity || 0;
                            const received = item.system_qty_received || 0;
                            const pending = Math.max(0, ordered - received);
                            const systemUnit = item.indent_unit || item.unit;
                            const status = pending === 0 ? 'complete' : received > 0 ? 'partial' : 'pending';
                            return (
                              <TableRow key={idx}>
                                <TableCell className="font-medium">{item.item_name}</TableCell>
                                <TableCell className="text-right">{ordered} {systemUnit}</TableCell>
                                <TableCell className="text-right text-green-600">{received} {systemUnit}</TableCell>
                                <TableCell className="text-right text-orange-600">{pending} {systemUnit}</TableCell>
                                <TableCell>
                                  <Badge variant={status === 'complete' ? 'default' : status === 'partial' ? 'secondary' : 'outline'}>
                                    {status === 'complete' ? 'Complete' : status === 'partial' ? 'Partial' : 'Pending'}
                                  </Badge>
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </div>

                    {/* GRN History */}
                    <div>
                      <p className="font-medium mb-3">Purchase History (GRNs)</p>
                      <div className="space-y-2">
                        {purchaseHistory.map((grn, idx) => (
                          <div key={idx} className="p-3 bg-muted/30 rounded-lg">
                            <div className="flex justify-between items-start">
                              <div>
                                <p className="font-mono font-medium">{grn.grn_number || grn.store_receipt_no}</p>
                                <p className="text-sm text-muted-foreground">{formatDate(grn.created_at)}</p>
                              </div>
                              <div className="text-right">
                                <p className="font-medium">{formatCurrency(grn.grand_total || 0)}</p>
                                <p className="text-xs text-muted-foreground">{grn.items?.length || 0} item(s)</p>
                              </div>
                            </div>
                            <div className="mt-2 text-sm">
                              {grn.items?.slice(0, 3).map((item, iIdx) => (
                                <span key={iIdx} className="inline-block mr-3 text-muted-foreground">
                                  {item.item_name}: {item.system_qty || item.quantity_received} {item.system_unit || item.unit}
                                </span>
                              ))}
                              {grn.items?.length > 3 && <span className="text-muted-foreground">+{grn.items.length - 3} more</span>}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {/* Loading History */}
                {loadingHistory && (
                  <div className="text-center py-4 text-muted-foreground">
                    Loading purchase history...
                  </div>
                )}

                {/* No Purchase History Message */}
                {!loadingHistory && purchaseHistory.length === 0 && selectedPO && ['approved', 'issued'].includes(selectedPO.status) && (
                  <div className="text-center py-4 text-muted-foreground border-t mt-4">
                    No GRNs created against this PO yet
                  </div>
                )}

                {/* Rejection Reason - show if PO was rejected */}
                {selectedPO.status === 'rejected' && selectedPO.rejection_reason && (
                  <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                    <p className="text-sm font-medium text-red-700">Rejection Reason:</p>
                    <p className="text-red-600">{selectedPO.rejection_reason}</p>
                  </div>
                )}

                {/* Edit History - show if PO has been edited */}
                {selectedPO.edit_history && selectedPO.edit_history.length > 0 && (
                  <div>
                    <p className="font-medium mb-2">Edit History</p>
                    <div className="space-y-2">
                      {selectedPO.edit_history.map((edit, idx) => (
                        <div key={idx} className="p-2 bg-muted/30 rounded text-sm">
                          <p className="text-muted-foreground">
                            {formatDate(edit.edited_at)} by {edit.edited_by_name}
                          </p>
                          {edit.edit_reason && <p>Reason: {edit.edit_reason}</p>}
                          <p className="text-xs text-muted-foreground">
                            Previous status: {edit.previous_status} | Total changed: ₹{edit.previous_total?.toLocaleString()} → ₹{edit.new_total?.toLocaleString()}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Clarification History */}
                {selectedPO.clarification_history && selectedPO.clarification_history.length > 0 && (
                  <div className="border rounded-lg p-4 bg-blue-50 border-blue-200">
                    <div className="flex items-center gap-2 mb-3">
                      <MessageCircle className="w-5 h-5 text-blue-600" />
                      <span className="font-semibold text-blue-800">Clarification Thread</span>
                    </div>
                    <div className="space-y-3">
                      {(() => {
                        // Build a unified timeline from legacy pairs + new action_type entries.
                        const fmtTs = (iso) => iso ? new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '';
                        const events = [];
                        (selectedPO.clarification_history || []).forEach((entry) => {
                          if (entry.action_type === 'edit' || entry.action_type === 'response') {
                            events.push({ at: entry.timestamp, entry });
                          } else {
                            // legacy pair shape: query + optional response
                            if (entry.requested_at) events.push({ at: entry.requested_at, entry, legacyRole: 'query' });
                            if (entry.responded_at) events.push({ at: entry.responded_at, entry, legacyRole: 'response' });
                          }
                        });
                        events.sort((a, b) => String(a.at || '').localeCompare(String(b.at || '')));
                        return events.map((ev, idx) => {
                          const e = ev.entry;
                          const isQuery = ev.legacyRole === 'query';
                          const isLegacyResponse = ev.legacyRole === 'response';
                          const isEdit = e.action_type === 'edit';
                          const isResponse = e.action_type === 'response';
                          if (isQuery) {
                            return (
                              <div key={`q-${idx}`} className="bg-white rounded p-2 border-l-2 border-blue-400">
                                <p className="text-xs text-blue-600 font-medium">
                                  {e.requested_by_name} requested clarification on {fmtTs(e.requested_at)}
                                </p>
                                <p className="text-sm mt-1 whitespace-pre-wrap">{e.remarks}</p>
                              </div>
                            );
                          }
                          if (isLegacyResponse) {
                            return (
                              <div key={`r-${idx}`} className="bg-green-50 rounded p-2 ml-6 border-l-2 border-green-400">
                                <p className="text-xs text-green-600 font-medium">
                                  Response on {fmtTs(e.responded_at)}
                                </p>
                                <p className="text-sm mt-1 whitespace-pre-wrap">{e.response}</p>
                              </div>
                            );
                          }
                          if (isEdit) {
                            const summary = e.edit_summary || [];
                            return (
                              <div key={`e-${idx}`} className="bg-amber-50 rounded p-2 ml-3 border-l-2 border-amber-400">
                                <p className="text-xs text-amber-700 font-medium">
                                  {e.actor_name} edited PO on {fmtTs(e.timestamp)}
                                  {typeof e.previous_total === 'number' && typeof e.new_total === 'number' && e.previous_total !== e.new_total && (
                                    <span className="ml-2 text-amber-900">
                                      Total: ₹{Number(e.previous_total).toLocaleString('en-IN')} → ₹{Number(e.new_total).toLocaleString('en-IN')}
                                    </span>
                                  )}
                                </p>
                                {summary.length > 0 && (
                                  <ul className="text-xs text-amber-900 mt-1 space-y-1 list-disc list-inside">
                                    {summary.map((s, si) => (
                                      <li key={si}>
                                        <span className="font-medium">{s.item_name || s.indent_number}</span>
                                        {' — '}
                                        {Object.entries(s.changes || {}).map(([f, v], ci) => (
                                          <span key={ci}>
                                            {ci > 0 ? ', ' : ''}
                                            <span className="font-semibold">{f}</span>: {String(v.before ?? '∅')} → {String(v.after ?? '∅')}
                                          </span>
                                        ))}
                                      </li>
                                    ))}
                                  </ul>
                                )}
                              </div>
                            );
                          }
                          if (isResponse) {
                            return (
                              <div key={`res-${idx}`} className="bg-green-50 rounded p-2 ml-6 border-l-2 border-green-400">
                                <p className="text-xs text-green-700 font-medium">
                                  {e.actor_name} responded on {fmtTs(e.timestamp)}
                                </p>
                                <p className="text-sm mt-1 whitespace-pre-wrap">{e.text}</p>
                              </div>
                            );
                          }
                          return null;
                        });
                      })()}
                    </div>

                    {/* Response Input - only show if status is clarification_requested and user is creator */}
                    {selectedPO.status === 'clarification_requested' && (
                      <div className="mt-4 pt-4 border-t border-blue-200">
                        <Label className="text-blue-800">Your Response *</Label>
                        <Textarea
                          placeholder="Enter your response to the clarification request..."
                          value={clarificationResponse}
                          onChange={(e) => setClarificationResponse(e.target.value)}
                          className="mt-2 bg-white"
                        />
                        <Button 
                          className="mt-2 bg-blue-600 hover:bg-blue-700"
                          onClick={handleRespondToClarification}
                          disabled={respondingToClarification || !clarificationResponse.trim()}
                        >
                          <Send className="w-4 h-4 mr-2" />
                          {respondingToClarification ? 'Submitting...' : 'Respond & Resubmit'}
                        </Button>
                        <p className="text-xs text-blue-700 mt-2">
                          Tip: To edit line items along with your response, use the Edit button on the PO list.
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpenDetails(false)}>Close</Button>
              {selectedPO && ['approved', 'issued', 'partial', 'received'].includes(selectedPO.status) && 
               selectedPO.line_items?.some(li => (li.system_qty_received || 0) > 0) && (
                <Button variant="outline" onClick={openReturnMaterial} data-testid="return-material-btn">
                  <RotateCcw className="w-4 h-4 mr-2" />
                  Return Material
                </Button>
              )}
              {selectedPO && ['approved', 'issued', 'partial', 'received'].includes(selectedPO.status) && (
                <Button onClick={() => { setOpenDetails(false); setPrintPO(selectedPO); }}>
                  <Printer className="w-4 h-4 mr-2" />
                  Print PO
                </Button>
              )}
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Return Material Dialog */}
        <Dialog open={openReturnDialog} onOpenChange={setOpenReturnDialog}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="return-material-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <RotateCcw className="w-5 h-5" />
                Return Material to Supplier
              </DialogTitle>
              <DialogDescription>
                {selectedPO?.po_number} | {selectedPO?.supplier_name}
              </DialogDescription>
            </DialogHeader>

            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Item</TableHead>
                  <TableHead className="text-right">Received</TableHead>
                  <TableHead className="text-right w-28">Return Qty</TableHead>
                  <TableHead className="w-48">Reason</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {returnItems.map((item, idx) => (
                  <TableRow key={item.item_id}>
                    <TableCell>
                      <div className="font-medium">{item.item_name}</div>
                      <div className="text-xs text-muted-foreground">{item.unit}</div>
                    </TableCell>
                    <TableCell className="text-right">{item.received}</TableCell>
                    <TableCell className="text-right">
                      <Input
                        type="number"
                        min={0}
                        max={item.received}
                        value={item.qty_to_return || ''}
                        onChange={(e) => {
                          const val = parseFloat(e.target.value) || 0;
                          setReturnItems(prev => prev.map((r, i) => i === idx ? { ...r, qty_to_return: val } : r));
                        }}
                        className="w-24 text-right"
                        placeholder="0"
                        data-testid={`return-qty-${idx}`}
                      />
                    </TableCell>
                    <TableCell>
                      <Input
                        value={item.reason}
                        onChange={(e) => setReturnItems(prev => prev.map((r, i) => i === idx ? { ...r, reason: e.target.value } : r))}
                        placeholder="Reason for return"
                        disabled={!item.qty_to_return}
                        data-testid={`return-reason-${idx}`}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            <p className="text-xs text-muted-foreground">Max returnable quantity will be validated by the system based on consumed and already returned quantities.</p>

            <DialogFooter>
              <Button variant="outline" onClick={() => setOpenReturnDialog(false)}>Cancel</Button>
              <Button
                onClick={handleSubmitReturn}
                disabled={submittingReturn || !returnItems.some(i => i.qty_to_return > 0)}
                data-testid="submit-return-btn"
              >
                {submittingReturn ? 'Submitting...' : 'Submit Return'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* PO Print Component */}
        {printPO && (
          <POPrint
            po={printPO}
            autoPrint={true}
            onPrintComplete={() => setPrintPO(null)}
          />
        )}

        {/* PO Revision Dialog */}
        <Dialog open={!!revisionPO} onOpenChange={(open) => !open && setRevisionPO(null)}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Edit2 className="w-5 h-5 text-orange-600" />
                Revise Purchase Order
              </DialogTitle>
              <DialogDescription>
                {revisionPO?.po_number} - Update quantities for over-receiving
              </DialogDescription>
            </DialogHeader>
            {revisionPO && (
              <div className="space-y-6 py-4">
                <div className="p-3 bg-orange-50 border border-orange-200 rounded-lg">
                  <p className="text-sm text-orange-800">
                    <strong>Note:</strong> This revision allows increasing PO quantities when the supplier delivers more than ordered. 
                    Quantities can only be increased, not decreased below the received amount.
                  </p>
                </div>

                {/* Line Items for Revision */}
                <div>
                  <Label className="text-sm font-medium mb-2 block">Line Items</Label>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Item</TableHead>
                        <TableHead className="text-right">Current Qty</TableHead>
                        <TableHead className="text-right">Received</TableHead>
                        <TableHead className="text-right w-32">New Qty</TableHead>
                        <TableHead className="text-right">New Amount</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {Object.entries(revisionItems).map(([itemId, data]) => (
                        <TableRow key={itemId}>
                          <TableCell>{data.item_name}</TableCell>
                          <TableCell className="text-right">
                            {data.current_qty} {data.unit}
                          </TableCell>
                          <TableCell className="text-right text-muted-foreground">
                            {data.received_qty} {data.unit}
                          </TableCell>
                          <TableCell className="text-right">
                            <Input
                              type="number"
                              step="0.01"
                              min={data.received_qty}
                              value={data.new_qty}
                              onChange={(e) => setRevisionItems(prev => ({
                                ...prev,
                                [itemId]: { ...prev[itemId], new_qty: e.target.value }
                              }))}
                              className="w-24 text-right"
                            />
                          </TableCell>
                          <TableCell className="text-right font-medium">
                            ₹{((parseFloat(data.new_qty) || 0) * data.unit_price).toLocaleString()}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>

                {/* Revision Reason */}
                <div className="space-y-2">
                  <Label htmlFor="revision-reason">Reason for Revision *</Label>
                  <Textarea
                    id="revision-reason"
                    placeholder="e.g., Supplier delivered extra quantity, approving excess delivery"
                    value={revisionReason}
                    onChange={(e) => setRevisionReason(e.target.value)}
                    rows={3}
                  />
                </div>

                {/* Summary */}
                {Object.values(revisionItems).some(i => i.new_qty !== i.current_qty) && (
                  <div className="p-3 bg-muted/50 rounded-lg">
                    <p className="font-medium mb-2">Changes Summary</p>
                    {Object.entries(revisionItems)
                      .filter(([, data]) => data.new_qty !== data.current_qty)
                      .map(([itemId, data]) => (
                        <div key={itemId} className="text-sm flex justify-between">
                          <span>{data.item_name}</span>
                          <span className="text-orange-600">
                            {data.current_qty} → {data.new_qty} {data.unit} 
                            (+{data.new_qty - data.current_qty})
                          </span>
                        </div>
                      ))
                    }
                  </div>
                )}
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setRevisionPO(null)} disabled={revising}>
                Cancel
              </Button>
              <Button 
                onClick={handleRevision} 
                disabled={revising || !revisionReason.trim()}
                className="bg-orange-600 hover:bg-orange-700"
                data-testid="confirm-revision-btn"
              >
                {revising ? 'Revising...' : 'Submit Revision'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Edit PO Dialog */}
        <Dialog open={!!editPO} onOpenChange={(open) => !open && setEditPO(null)}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Edit2 className="w-5 h-5 text-blue-600" />
                Edit Purchase Order
              </DialogTitle>
              <DialogDescription>
                {editPO?.po_number} - {editPO?.supplier_name}
                {editPO?.status === 'rejected' && (
                  <span className="ml-2 text-red-500">(Rejected - editing will resubmit for approval)</span>
                )}
                {['approved', 'issued', 'partial'].includes(editPO?.status) && (
                  <span className="ml-2 text-orange-500">(Will require re-approval)</span>
                )}
              </DialogDescription>
            </DialogHeader>
            {editPO && (
              <div className="space-y-6 py-4">
                {/* Rejection reason display */}
                {editPO.status === 'rejected' && editPO.rejection_reason && (
                  <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                    <p className="text-sm font-medium text-red-700">Rejection Reason:</p>
                    <p className="text-red-600 text-sm">{editPO.rejection_reason}</p>
                  </div>
                )}

                {/* Last open clarification query — shown when editing from clarification_requested */}
                {editPO.status === 'clarification_requested' && (() => {
                  const history = editPO.clarification_history || [];
                  let lastQuery = null;
                  for (let i = history.length - 1; i >= 0; i--) {
                    const ent = history[i];
                    if (ent.action_type === 'edit' || ent.action_type === 'response') continue;
                    if (ent.response === null || ent.response === undefined) { lastQuery = ent; break; }
                  }
                  if (!lastQuery) return null;
                  return (
                    <div className="p-3 bg-purple-50 border border-purple-200 rounded-lg" data-testid="edit-clarification-query-banner">
                      <p className="text-sm font-medium text-purple-700">
                        {lastQuery.requested_by_name} asked (
                        {lastQuery.requested_at ? new Date(lastQuery.requested_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : ''}
                        ):
                      </p>
                      <p className="text-purple-800 text-sm mt-1 whitespace-pre-wrap">{lastQuery.remarks}</p>
                    </div>
                  );
                })()}

                {/* Line Items Table */}
                <div className="border rounded-lg overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-muted/50">
                        <TableHead className="w-40">Item</TableHead>
                        <TableHead className="w-32">Supplier Name</TableHead>
                        <TableHead className="w-20 text-right">PO Qty</TableHead>
                        <TableHead className="w-20 text-center">System UOM</TableHead>
                        <TableHead className="w-24">Supplier Qty *</TableHead>
                        <TableHead className="w-20 text-center">Supplier UOM</TableHead>
                        <TableHead className="w-24">Price *</TableHead>
                        <TableHead className="w-24 text-right">Amount</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {editLineItems.map((item, idx) => (
                        <TableRow key={item.indent_id}>
                          <TableCell>
                            <div className="font-medium">{item.item_name}</div>
                            <div className="text-xs text-muted-foreground">{item.indent_number}</div>
                            {/* Show original indent qty as reference */}
                            {item.indent_quantity && item.indent_quantity !== (item.system_qty_ordered || item.indent_quantity) && (
                              <div className="text-xs text-blue-600">Indent: {item.indent_quantity} {item.indent_unit}</div>
                            )}
                          </TableCell>
                          <TableCell>
                            <Input
                              value={item.supplier_item_name}
                              onChange={(e) => updateEditLineItem(idx, 'supplier_item_name', e.target.value)}
                              className="h-8"
                              placeholder="Generic name"
                            />
                          </TableCell>
                          <TableCell className="text-right font-medium">
                            {item.system_qty_ordered || item.indent_quantity}
                          </TableCell>
                          <TableCell className="text-center">
                            <span className="px-2 py-1 bg-gray-100 rounded text-sm">
                              {item.indent_unit}
                            </span>
                          </TableCell>
                          <TableCell>
                            <Input
                              type="number"
                              value={item.quantity}
                              onChange={(e) => updateEditLineItem(idx, 'quantity', e.target.value)}
                              className="h-8"
                              min="0"
                              step="0.01"
                            />
                          </TableCell>
                          <TableCell className="text-center">
                            <Select
                              value={item.unit || ''}
                              onValueChange={(v) => updateEditLineItem(idx, 'unit', v)}
                            >
                              <SelectTrigger className="h-8 w-[100px]" data-testid={`edit-po-supplier-uom-${idx}`}>
                                <SelectValue placeholder="UOM" />
                              </SelectTrigger>
                              <SelectContent>
                                {(Array.isArray(uomList) ? uomList : []).map(u => (
                                  <SelectItem key={u.code || u.name} value={u.code || u.name}>{u.code || u.name}</SelectItem>
                                ))}
                                {/* Include current value if not in list */}
                                {item.unit && !(Array.isArray(uomList) ? uomList : []).some(u => (u.code || u.name) === item.unit) && (
                                  <SelectItem value={item.unit}>{item.unit}</SelectItem>
                                )}
                              </SelectContent>
                            </Select>
                          </TableCell>
                          <TableCell>
                            <Input
                              type="number"
                              value={item.unit_price}
                              onChange={(e) => updateEditLineItem(idx, 'unit_price', e.target.value)}
                              className="h-8"
                              min="0"
                              step="0.01"
                              placeholder="₹"
                            />
                          </TableCell>
                          <TableCell className="text-right font-medium">
                            {formatCurrency(item.amount || 0)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>

                {/* Other Charges */}
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label>Other Charges (Taxable)</Label>
                    <Input
                      type="number"
                      value={editOtherChargesTaxable}
                      onChange={(e) => setEditOtherChargesTaxable(e.target.value)}
                      className="mt-1"
                      min="0"
                      step="0.01"
                    />
                  </div>
                  <div>
                    <Label>GST Rate (%)</Label>
                    <Input
                      type="number"
                      value={editGstRate}
                      onChange={(e) => setEditGstRate(e.target.value)}
                      className="mt-1"
                      min="0"
                      max="100"
                    />
                  </div>
                  <div>
                    <Label>Other Charges (Non-Taxable)</Label>
                    <Input
                      type="number"
                      value={editOtherChargesNonTaxable}
                      onChange={(e) => setEditOtherChargesNonTaxable(e.target.value)}
                      className="mt-1"
                      min="0"
                      step="0.01"
                    />
                  </div>
                </div>

                {/* Totals */}
                <div className="flex justify-end">
                  <div className="w-64 space-y-1 text-sm">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Sub Total:</span>
                      <span>{formatCurrency(calculateEditTotals().subTotal)}</span>
                    </div>
                    {editOtherChargesTaxable > 0 && (
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Other Charges (Taxable):</span>
                        <span>{formatCurrency(parseFloat(editOtherChargesTaxable))}</span>
                      </div>
                    )}
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">GST ({editGstRate}%):</span>
                      <span>{formatCurrency(calculateEditTotals().gstAmount)}</span>
                    </div>
                    {editOtherChargesNonTaxable > 0 && (
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Other Charges:</span>
                        <span>{formatCurrency(parseFloat(editOtherChargesNonTaxable))}</span>
                      </div>
                    )}
                    <div className="flex justify-between font-bold border-t pt-1">
                      <span>Grand Total:</span>
                      <span>{formatCurrency(calculateEditTotals().total)}</span>
                    </div>
                  </div>
                </div>

                {/* Edit Reason - required for approved POs */}
                {['approved', 'issued', 'partial'].includes(editPO.status) && (
                  <div>
                    <Label className="text-orange-600">Edit Reason (Required) *</Label>
                    <Textarea
                      value={editReason}
                      onChange={(e) => setEditReason(e.target.value)}
                      placeholder="Explain why this approved PO needs to be modified..."
                      className="mt-1"
                      rows={2}
                    />
                  </div>
                )}

                {/* Clarification response — required when editing from clarification_requested */}
                {editPO.status === 'clarification_requested' && (
                  <div>
                    <Label className="text-purple-700">Your Response to Clarification (Required) *</Label>
                    <Textarea
                      value={editClarificationResponse}
                      onChange={(e) => setEditClarificationResponse(e.target.value)}
                      placeholder="Explain the changes you made to address the clarification request..."
                      className="mt-1"
                      rows={3}
                      data-testid="edit-clarification-response-input"
                    />
                  </div>
                )}
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditPO(null)} disabled={editing}>
                Cancel
              </Button>
              <Button 
                onClick={handleEditPO} 
                disabled={editing}
                className="bg-blue-600 hover:bg-blue-700"
                data-testid="confirm-edit-btn"
              >
                {editing ? 'Saving...' : (
                  editPO?.status === 'clarification_requested'
                    ? 'Save Edit & Respond'
                    : (['approved', 'issued', 'partial', 'rejected'].includes(editPO?.status)
                      ? 'Save & Submit for Approval'
                      : 'Save Changes')
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Close PO Dialog */}
        <Dialog open={!!closePODialog} onOpenChange={(open) => !open && setClosePODialog(null)}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <XCircle className="w-5 h-5 text-red-600" />
                Close Purchase Order
              </DialogTitle>
              <DialogDescription>
                Close PO <span className="font-mono font-semibold">{closePODialog?.po_number}</span> manually.
                {closePODialog && (
                  <div className="mt-2 p-3 bg-amber-50 border border-amber-200 rounded-lg text-amber-800 text-sm">
                    <p className="font-medium">Note:</p>
                    <p>This will mark the PO as complete even if all quantities haven't been received. 
                    Any pending quantities will be recorded as short-closed.</p>
                  </div>
                )}
              </DialogDescription>
            </DialogHeader>
            
            {closePODialog && (
              <div className="space-y-4 py-4">
                {/* Show pending items */}
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Pending Items:</Label>
                  <div className="max-h-40 overflow-y-auto border rounded-lg p-2 bg-muted/30">
                    {closePODialog.line_items?.map((item, idx) => {
                      const ordered = item.system_qty_ordered || item.indent_quantity || item.quantity || 0;
                      const received = item.system_qty_received || 0;
                      const pending = Math.max(0, ordered - received);
                      const unit = item.indent_unit || item.unit;
                      return (
                        <div key={idx} className="flex justify-between items-center py-1 text-sm">
                          <span className="truncate flex-1">{item.item_name}</span>
                          <span className={`ml-2 font-mono ${pending > 0 ? 'text-red-600' : 'text-green-600'}`}>
                            {pending > 0 ? `${pending} ${unit} pending` : 'Fully received'}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Reason input */}
                <div className="space-y-2">
                  <Label htmlFor="closeReason" className="text-sm font-medium">
                    Reason for closing <span className="text-red-500">*</span>
                  </Label>
                  <Textarea
                    id="closeReason"
                    placeholder="e.g., Short shipment accepted - supplier unable to deliver remaining quantity"
                    value={closeReason}
                    onChange={(e) => setCloseReason(e.target.value)}
                    rows={3}
                    className="resize-none"
                  />
                </div>
              </div>
            )}

            <DialogFooter>
              <Button variant="outline" onClick={() => setClosePODialog(null)} disabled={closingPO}>
                Cancel
              </Button>
              <Button 
                onClick={handleClosePO} 
                disabled={closingPO || !closeReason.trim()}
                className="bg-red-600 hover:bg-red-700"
              >
                {closingPO ? 'Closing...' : 'Close PO'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete PO Confirmation Dialog */}
        <Dialog open={!!deletePODialog} onOpenChange={(open) => !open && setDeletePODialog(null)}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-red-600">
                <Trash2 className="w-5 h-5" />
                Delete Purchase Order
              </DialogTitle>
              <DialogDescription>
                Are you sure you want to delete <span className="font-mono font-semibold">{deletePODialog?.po_number}</span>?
              </DialogDescription>
            </DialogHeader>
            {deletePODialog && (
              <div className="py-4">
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-sm text-red-800">
                    <strong>Warning:</strong> This action cannot be undone. 
                    {['approved', 'issued', 'partial'].includes(deletePODialog.status) && (
                      <span> Deleting this approved PO will restore indent quantities and release po_issued amounts.</span>
                    )}
                  </p>
                </div>
                <div className="mt-4 space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Supplier:</span>
                    <span className="font-medium">{deletePODialog.supplier_name}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Status:</span>
                    <Badge className={statusColors[deletePODialog.status]}>{statusLabels[deletePODialog.status]}</Badge>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Total Amount:</span>
                    <span className="font-medium">₹{(deletePODialog.total_amount || 0).toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Items:</span>
                    <span className="font-medium">{deletePODialog.line_items?.length || 0}</span>
                  </div>
                </div>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setDeletePODialog(null)} disabled={deletingPO}>
                Cancel
              </Button>
              <Button 
                onClick={handleDeletePO} 
                disabled={deletingPO}
                className="bg-red-600 hover:bg-red-700"
              >
                {deletingPO ? 'Deleting...' : 'Delete PO'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Price History Popup */}
        <PriceHistoryPopup
          open={priceHistoryOpen}
          onClose={() => {
            setPriceHistoryOpen(false);
            setPriceHistoryItem(null);
          }}
          itemId={priceHistoryItem?.itemId}
          itemName={priceHistoryItem?.itemName}
          supplierId={priceHistoryItem?.supplierId}
          supplierName={priceHistoryItem?.supplierName}
          currentPrice={priceHistoryItem?.currentPrice}
          unit={priceHistoryItem?.unit}
        />
      </div>
    </DashboardLayout>
  );
}
